// src/utils/quote/quoteConstants.js
//
// Option `value` fields for LUJUUSLUOKKA / RAEKOKO / NOTKEUS / KAYTTO_IKA
// are NUMBERS (matching the corresponding betoni* table FK ids), not strings.
// MUI's non-native `<TextField select>` preserves the option-value type on
// pick, and JSON.stringify/parse preserves number types through the
// localStorage round-trip, so strict equality checks against the IDs are
// type-safe end-to-end. `""` is the "En tiedä" sentinel and serialises to
// NULL on the wire.

// `typicalClass` is the rasitusluokka the avustin resolves to with default answers,
// shown as a small hint in the dropdown next to the label. `description` is a short
// blurb rendered under the label. Both are optional — items without them fall back
// to a plain label-only row.
//
// i18n: these 5 OPTIONS arrays are functions of `t` — their `value`s (and
// `typicalClass`, which mirrors betoniRasitusLuokat wire codes) are DATA and stay
// untouched; only `label` / `description` go through `t()`. Consumers span three
// extraction tasks (16, 18, 19) so this conversion was deliberately deferred until
// a task could touch all of them — see src/i18n/fi.js's
// wizard.quoteConstants.options comment for the full consumer list.
// @param {(key: string) => string} t
export function kayttokohdeOptions(t) {
    return [
        {
            value: "anturat",
            label: t("wizard.quoteConstants.options.kayttokohde.anturat.label"),
            typicalClass: "XC3-4, XF1",
            description: t("wizard.quoteConstants.options.kayttokohde.anturat.description"),
        },
        {
            value: "sokkeli",
            label: t("wizard.quoteConstants.options.kayttokohde.sokkeli.label"),
            typicalClass: "XC3-4, XF1",
            description: t("wizard.quoteConstants.options.kayttokohde.sokkeli.description"),
        },
        {
            value: "harkko",
            label: t("wizard.quoteConstants.options.kayttokohde.harkko.label"),
            typicalClass: "XC3-4, XF1",
            description: t("wizard.quoteConstants.options.kayttokohde.harkko.description"),
        },
        {
            value: "lattia",
            label: t("wizard.quoteConstants.options.kayttokohde.lattia.label"),
            typicalClass: "XC1",
            description: t("wizard.quoteConstants.options.kayttokohde.lattia.description"),
        },
        {
            value: "seinat",
            label: t("wizard.quoteConstants.options.kayttokohde.seinat.label"),
            typicalClass: "XC1",
            description: t("wizard.quoteConstants.options.kayttokohde.seinat.description"),
        },
        {
            value: "holvi",
            label: t("wizard.quoteConstants.options.kayttokohde.holvi.label"),
            typicalClass: "XC1",
            description: t("wizard.quoteConstants.options.kayttokohde.holvi.description"),
        },
        {
            value: "pilarit",
            label: t("wizard.quoteConstants.options.kayttokohde.pilarit.label"),
            typicalClass: "XC1",
            description: t("wizard.quoteConstants.options.kayttokohde.pilarit.description"),
        },
        {
            value: "portaat",
            label: t("wizard.quoteConstants.options.kayttokohde.portaat.label"),
            description: t("wizard.quoteConstants.options.kayttokohde.portaat.description"),
        },
        {
            value: "muu",
            label: t("wizard.quoteConstants.options.kayttokohde.muu.label"),
            description: t("wizard.quoteConstants.options.kayttokohde.muu.description"),
        },
    ];
}

// Values are betoniLujuus.lujuusId. "" = "En tiedä" → NULL on the wire. The grade
// codes themselves ("C20/25", …) are an international notation, not language —
// only the "En tiedä" / "Muu" sentinels go through `t()`.
export function lujuusluokkaOptions(t) {
    return [
        { value: "", label: t("wizard.quoteConstants.options.lujuusluokka.unknown") },
        { value: 1,  label: "C20/25" },
        { value: 2,  label: "C25/30" },
        { value: 3,  label: "C30/37" },
        { value: 4,  label: "C35/45" },
        { value: 5,  label: "C40/50" },
        { value: 6,  label: "C45/55" },
        { value: 7,  label: "C50/60" },
        { value: 8,  label: t("wizard.quoteConstants.options.lujuusluokka.muu") },
    ];
}

// Values are betoniRaeKoko.raeKokoId. raeKokoId=2 = "hieno 16 mm".
export function raekokoOptions(t) {
    return [
        { value: "", label: t("wizard.quoteConstants.options.raekoko.unknown") },
        { value: 4,  label: "8 mm" },
        { value: 1,  label: "16 mm" },
        { value: 2,  label: t("wizard.quoteConstants.options.raekoko.hieno16") },
        { value: 3,  label: "32 mm" },
        { value: 5,  label: t("wizard.quoteConstants.options.raekoko.muu") },
    ];
}

// Values are betoniNotkeus.notkeusId. notkeusId=10 = "Muu".
export function notkeusOptions(t) {
    return [
        { value: "", label: t("wizard.quoteConstants.options.notkeus.unknown") },
        { value: 2,  label: "S2" },
        { value: 3,  label: "S3" },
        { value: 4,  label: "S4" },
        { value: 10, label: t("wizard.quoteConstants.options.notkeus.muu") },
    ];
}

// Values are betoniKayttoIka.kayttoIkaId. 1 = 50 vuotta, 2 = 100 vuotta.
// The wizard only exposes 50v/100v today (200v exists in the table but is unused).
export function kayttoIkaOptions(t) {
    return [
        { value: 1, label: t("wizard.quoteConstants.options.kayttoIka.v50") },
        { value: 2, label: t("wizard.quoteConstants.options.kayttoIka.v100") },
    ];
}

// Named "Muu" sentinel IDs for each reference table — avoid magic numbers at
// call sites. Values match the corresponding row id in dbo.betoni* tables.
export const LUJUUS_ID_MUU = 8;
export const RAE_KOKO_ID_MUU = 5;
export const NOTKEUS_ID_MUU = 10;
export const KAYTTO_IKA_ID_50V = 1;
export const KAYTTO_IKA_ID_100V = 2;

// raeKokoId 1 ("16 mm") and 3 ("32 mm") are the regular aggregates that can't
// be reliably pumped through > 7 m of extension line (supplier-specific rule).
export const RAE_KOKO_IDS_FORBIDDEN_ON_LONG_LINE = [1, 3];

// Shared dropdown-label lookup. Returns `fallback` when `value` isn't in
// `options`, and also when a matched option's `label` is itself falsy (e.g.
// an empty string) — a matched-but-blank label is never more useful than the
// fallback. Callers pass the raw slug as fallback (ValuListCard / ValuDetail)
// when they want legacy/unknown values to display as-is rather than collapse
// to a placeholder.
export function labelFor(options, value, fallback = "") {
    return options.find((o) => o.value === value)?.label || fallback;
}

// STEP_LABELS is wizard-only (its single consumer is QuoteStepper.jsx).
// @param {(key: string) => string} t
export function stepLabels(t) {
    return [
        t("wizard.quoteConstants.stepLabels.osoite"),
        t("wizard.quoteConstants.stepLabels.pumppu"),
        t("wizard.quoteConstants.stepLabels.betoni"),
        t("wizard.quoteConstants.stepLabels.lisatiedot"),
        t("wizard.quoteConstants.stepLabels.tarkista"),
    ];
}

// BETONILAATU_DISCLAIMER was removed — its two consumers (StepConcrete.jsx,
// SpecGuideResult.jsx) now call t("wizard.quoteConstants.betoniLaatuDisclaimer")
// directly rather than importing a constant from this shared-and-partly-static file.

export const INITIAL_DRAFT = {
    osoite: "",
    placeId: null, lat: null, lng: null,
    formattedAddress: null,
    feasibility: null,
    maaraM3: "",
    kayttokohde: "",
    lujuusId: "",
    raeKokoId: "",
    notkeusId: "",
    rasitusluokka: "",
    betonikuvaus: "",
    // Spec-guide state — `specMode` is null (no choice), "guided" (avustin),
    // or "manual" (user filled the rasitusluokka/lujuus fields directly).
    // `guidedAnswers` is the answer map keyed by tree node id (plus optional
    // käyttökohde-specific post-question keys). `kayttoIkaId` is the
    // betoniKayttoIka FK: 1 = 50v (default), 2 = 100v.
    specMode: null,
    guidedAnswers: {},
    betoniLaatuSelvitys: null,
    kayttoIkaId: 1,
    puomi: "",
    linjanPituus: "",
    kesto: "",
    ajoOhjeet: "",
    kuvaus: "",
    // Gate 1 acceptance (data sharing + non-binding ack). The accepted version is
    // fetched from the DB (BETONIJERRY_REQUEST_CONSENT) by RequestForm and stamped
    // onto each pumppuRequest at send time (backend persists termsVersion +
    // termsAcceptedAt). See src/utils/legal/acceptanceCopy.js.
    termsAccepted: false,
    // Attachment rows enriched with fresh blob SAS URLs. Not persisted to
    // localStorage (the wizard pre-claims the server draft on step 4 → 5 for
    // authenticated users, and the canonical store is the attachments table).
    attachments: [],
};
